// Static sample of GET /ad-templates (same response shape, trimmed to the
// fields this page uses) until the public backend serves real templates.
// Video templates are kept only for their cover image: `videoUrl` and
// `durationSeconds` are dropped and `creativeFormat` is "image".

export type GeoDisplay = { key: string; name: string; type: string };

// Same shape as a console ad variant (rest-api VARIANT_RESPONSE_FIELDS). Fields
// the generator may not fill (targeting, budget) are optional.
export type AdTemplateVariant = {
  _id: string;
  variantIndex: number;
  variantStatus: string;
  adType: string;
  adName?: string;
  headline: string;
  headlineDescription: string;
  adDescription: string;
  callToAction?: string;
  ctaButton?: string;
  websiteUrl?: string;
  imageUrl: string;
  creativeFormat?: string;
  aspectRatio?: string;
  budgetType?: string;
  dailyBudget?: string | number;
  minAge?: string | number;
  maxAge?: string | number;
  geoLocationDisplay?: GeoDisplay[];
  prediction?: { currency?: string };
  selected?: {
    budgetType?: string;
    dailyBudget?: number;
    lifetimeBudget?: number;
    durationDays?: number;
  };
};

export type AdTemplate = {
  _id: string;
  prompt: string;
  templateCount: number;
  aspectRatio: string;
  creativeFormat: string;
  status: string;
  createdAt: string;
  variants: AdTemplateVariant[];
};

export type AdTemplatesResponse = {
  status: boolean;
  data: AdTemplate[];
};

const city = (key: string, name: string): GeoDisplay => ({ key, name, type: 'city' });
const DELHI: GeoDisplay = { key: '1728', name: 'Delhi', type: 'region' };
const MUMBAI = city('1035921', 'Mumbai');
const BANGALORE = city('1017930', 'Bangalore');
const PUNE = city('1039952', 'Pune');
const HYDERABAD = city('1027234', 'Hyderabad');
const GURGAON = city('1026298', 'Gurgaon');
const CHENNAI = city('1021534', 'Chennai');
const AHMEDABAD = city('1015741', 'Ahmedabad');
const SURAT = city('1044419', 'Surat');
const JAIPUR = city('1027633', 'Jaipur');
const KOTA = city('1031790', 'Kota');
const INDORE = city('1027344', 'Indore');
const LUCKNOW = city('1033376', 'Lucknow');

const INR = { currency: 'INR' };
const daily = (dailyBudget: number) => ({
  budgetType: 'daily_budget',
  dailyBudget,
  durationDays: 7,
});

const FITPASS_PROMPT =
  'Create a high-converting ad for fitpass . This is my website URL  fitpass . My target audience are gym lover .';
const AISENSY_PROMPT =
  'Create a high-converting ad for Aisensy . This is my website URL https://aisensy.com . My target audience are company .';
const AISENSY_GEO = [BANGALORE, MUMBAI, DELHI, GURGAON, PUNE, HYDERABAD];

const adTemplatesResponse: AdTemplatesResponse = {
  status: true,
  data: [
    {
      _id: '6ab3d0dc6d5b554b5a118990',
      prompt: AISENSY_PROMPT,
      templateCount: 1,
      aspectRatio: '9:16',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-09-23T13:10:00.000000',
      variants: [
        {
          _id: '6ab3d0dc6d5b554b5a118998',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'website_to_whatsapp',
          adName: 'WhatsApp Broadcasting',
          headline: 'One broadcast. Your whole list.',
          headlineDescription: 'Official WhatsApp Business API for stores',
          adDescription:
            'Reaching every customer on your list takes just one broadcast now.\n\nWhen your store has a new collection or festive drop, you want it landing directly in personal chats. You can send rich carousels and promotional campaigns straight to your entire buyer list using the Official WhatsApp Business API.',
          callToAction: 'SHOP_NOW',
          ctaButton: 'SHOP_NOW',
          websiteUrl: 'https://aisensy.com',
          imageUrl:
            'https://whatsapp-ai-assets.s3.amazonaws.com/generated_images/9399973e46b14344b91803a894a1b498.jpg',
          creativeFormat: 'image',
          aspectRatio: '9:16',
          budgetType: 'daily_budget',
          dailyBudget: '1500',
          minAge: '24',
          maxAge: '50',
          geoLocationDisplay: [
            DELHI,
            MUMBAI,
            BANGALORE,
            city('1026297', 'Gurugram'),
            JAIPUR,
            AHMEDABAD,
          ],
          prediction: INR,
          selected: daily(1500),
        },
      ],
    },
    {
      _id: '6ac87753ddeac4d2b780bdc7',
      prompt: FITPASS_PROMPT,
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-09T05:10:43.137000',
      variants: [
        {
          _id: '6ac8776b1eb7244596627bae',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'FitPass Membership',
          headline: 'FitPass Membership',
          headlineDescription: 'Fitness freedom, one pass.',
          adDescription:
            "Ek hi gym se bore ho gaye ho? Ab apni fitness journey ko next level pe le chalo!\n\nFitPass ke saath, aapko milti hai freedom to explore hundreds of top gyms and fitness studios across Mumbai, Bangalore, aur Delhi. Every day can be a new challenge, a new vibe.\n\nTap 'Send Message' to connect on WhatsApp and unlock unlimited fitness options!",
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/fitpass-6ac87753ddeac4d2b780bdc7-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '900',
          minAge: '22',
          maxAge: '40',
          geoLocationDisplay: [MUMBAI, BANGALORE, DELHI, PUNE, HYDERABAD, GURGAON],
          prediction: INR,
          selected: daily(900),
        },
      ],
    },
    {
      _id: '6ac8774cddeac4d2b780bdc5',
      prompt: FITPASS_PROMPT,
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-09T05:10:36.891000',
      variants: [
        {
          _id: '6ac877691eb7244596627bad',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'FitPass Membership',
          headline: 'FitPass Membership',
          headlineDescription: 'Your city, your gyms, your way.',
          adDescription:
            "Feeling stuck in the same gym routine, day after day? We've got something that changes everything.\n\nWith FitPass, you can explore hundreds of gyms and fitness centres across Mumbai, Bangalore, and Delhi. Imagine trying a new yoga studio one day, hitting the weights at a different spot the next, or joining a high-energy Zumba class.\n\nIt's time to shake things up and find what truly motivates you. Tap to watch our video and see how FitPass opens up a whole new world of fitness for you!",
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/fitpass-6ac8774cddeac4d2b780bdc5-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '900',
          minAge: '24',
          maxAge: '42',
          geoLocationDisplay: [MUMBAI, BANGALORE, DELHI, GURGAON, PUNE, HYDERABAD],
          prediction: INR,
          selected: daily(900),
        },
      ],
    },
    {
      _id: '6ac8774a72756eea3857d143',
      prompt: FITPASS_PROMPT,
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-09T05:10:34.631000',
      variants: [
        {
          _id: '6ac877611eb7244597593705',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'Fitpass Membership',
          headline: 'Ek Card. Gym Switch, Jab Chahe.',
          headlineDescription: 'Your city, your gym. Endless fitness options.',
          adDescription:
            "One gym, same four walls... but your workout doesn't have to be stuck there.\n\nImagine unlimited access to a whole network of gyms and fitness classes across your city. From power-packed gym sessions to invigorating yoga, discover new ways to move and challenge yourself.\n\nReady to explore? Tap 'Learn More' to find your next favorite workout spot with Fitpass.",
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/fitpass-6ac8774a72756eea3857d143-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '900',
          minAge: '22',
          maxAge: '40',
          geoLocationDisplay: [MUMBAI, DELHI, BANGALORE, GURGAON, PUNE, HYDERABAD],
          prediction: INR,
          selected: daily(900),
        },
      ],
    },
    {
      _id: '6ac77183aaccb1f33a55cb99',
      prompt:
        'Create a high-converting ad for aisnesy . This is my website URL https://aisensy.com . My target audience are company .',
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-08T10:33:39.479000',
      variants: [
        {
          _id: '6ac771b51eb7247221571ee1',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'website_to_whatsapp',
          adName: 'WhatsApp Marketing & Broadcasting',
          headline: 'Cart chhoda? Broadcast bhej do',
          headlineDescription: 'Grow sales with AI-powered WhatsApp engagement.',
          adDescription:
            "Imagine reaching 2.6 billion people. That's the power of WhatsApp for your business.\n\nAiSensy helps D2C and e-commerce brands like yours turn conversations into conversions with smart, automated WhatsApp marketing. From abandoned cart reminders to instant customer support, automate every touchpoint.\n\nReady to unlock serious growth? Click 'Learn More' to connect with us on WhatsApp and see how.",
          callToAction: 'SHOP_NOW',
          ctaButton: 'SHOP_NOW',
          websiteUrl: 'https://aisensy.com',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/aisensy-6ac77183aaccb1f33a55cb99-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '800',
          minAge: '24',
          maxAge: '45',
          geoLocationDisplay: [MUMBAI, DELHI, BANGALORE, AHMEDABAD, PUNE, SURAT],
          prediction: INR,
          selected: daily(800),
        },
      ],
    },
    {
      _id: '6ac76d9be37eac5bccff5960',
      prompt: AISENSY_PROMPT,
      templateCount: 3,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-08T10:17:00.021000',
      variants: [
        {
          _id: '6ac76da71eb7241ec4674696',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'WhatsApp Business API (Free setup + Green Tick)',
          headline: 'WhatsApp Business API',
          headlineDescription: 'Official tools to scale your D2C brand.',
          adDescription:
            'Getting your D2C brand on the official WhatsApp Business API is simpler than you think.\n\nGet your green tick, automate your customer engagement, and run campaigns that actually reach your buyers where they spend their time.\n\nSend us a message on WhatsApp to see how it works for your store.',
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://whatsapp-ai-assets.s3.amazonaws.com/generated_images/622e5e0151d64d3c9c320f17a49f3488.jpg',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '800',
          minAge: '24',
          maxAge: '45',
          geoLocationDisplay: AISENSY_GEO,
          prediction: INR,
          selected: daily(800),
        },
        {
          _id: '6ac76da71eb7241ec4674697',
          variantIndex: 1,
          variantStatus: 'completed',
          adType: 'website_to_whatsapp',
          adName: 'WhatsApp Broadcasting',
          headline: '800 leads. 12 callers. One inbox.',
          headlineDescription: 'Handle admission rush on WhatsApp',
          adDescription:
            'When admission season peaks, the enquiry register fills up faster than your team can dial.\n\nInstead of letting warm leads go cold while your counsellors are stuck on calls, broadcast updates and course details straight to WhatsApp. Everyone gets a reply instantly, and your team only talks to the students ready to join.\n\nSee how your institute can manage hundreds of daily enquiries without missing a single student.',
          callToAction: 'SHOP_NOW',
          ctaButton: 'SHOP_NOW',
          websiteUrl: 'https://aisensy.com',
          imageUrl:
            'https://whatsapp-ai-assets.s3.amazonaws.com/generated_images/2af20bd00fab4f4baf54ee26ee64ca48.jpg',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '700',
          minAge: '25',
          maxAge: '55',
          geoLocationDisplay: [DELHI, KOTA, HYDERABAD, PUNE, INDORE, LUCKNOW],
          prediction: INR,
          selected: daily(700),
        },
        {
          _id: '6ac76da71eb7241ec4674698',
          variantIndex: 2,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'AI Ads Manager (Click to WhatsApp Ads)',
          headline: 'Your Instagram ad opens on WhatsApp',
          headlineDescription: 'Turn Instagram ad clicks into WhatsApp chats',
          adDescription:
            'Stop losing leads between your Instagram ad and your website form.\n\nBring people straight from your Instagram feed into a direct WhatsApp chat where conversations actually turn into sales. No forms to fill, no drop-offs—just high-intent prospects landing right in your chat list.\n\nSend us a quick message on WhatsApp to see how it works for your business.',
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://whatsapp-ai-assets.s3.amazonaws.com/generated_images/47d81ece94ce455dba9d0150a362f89e.jpg',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '1000',
          minAge: '24',
          maxAge: '55',
          geoLocationDisplay: [MUMBAI, DELHI, AHMEDABAD, JAIPUR, SURAT, CHENNAI],
          prediction: INR,
          selected: daily(1000),
        },
      ],
    },
    {
      _id: '6ac76be7e37eac5bccff5803',
      prompt: AISENSY_PROMPT,
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-08T10:09:43.978000',
      variants: [
        {
          _id: '6ac76bef1eb7247bd138dc13',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'WhatsApp Business API (Free setup + Green Tick)',
          headline: 'WhatsApp Business API',
          headlineDescription: 'Turn chats into sales. Automate WhatsApp with AiSensy.',
          adDescription:
            "Losing out on sales because manual WhatsApp replies just can't keep up?\n\nIt's time to supercharge your customer engagement. Aisensy helps D2C brands like yours automate WhatsApp conversations, deliver instant support, and recover abandoned carts – all on the Official WhatsApp Business API.\n\nReady to turn those waiting messages into conversions? Tap 'Send Message' to explore how Aisensy can transform your WhatsApp strategy.",
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/aisensy-6ac76be7e37eac5bccff5803-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '1200',
          minAge: '24',
          maxAge: '45',
          geoLocationDisplay: AISENSY_GEO,
          prediction: INR,
          selected: daily(1200),
        },
      ],
    },
    {
      _id: '6ac76be65560a38ce95633cd',
      prompt: AISENSY_PROMPT,
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-08T10:09:42.945000',
      variants: [
        {
          _id: '6ac76bef1eb7247bd138dc12',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'WhatsApp Business API (Free setup + Green Tick)',
          headline: 'WhatsApp Business API',
          headlineDescription: 'Automate engagement, drive sales on WhatsApp.',
          adDescription:
            "Most D2C and e-commerce customers are already on WhatsApp. Are you connecting with them effectively?\n\nWith Aisensy, you get an AI-powered WhatsApp marketing platform, built on the Official WhatsApp Business API. Automate engagement, nurture leads, and drive real growth for your brand where your customers spend their time.\n\nReady to make WhatsApp your most powerful channel? Tap 'Send Message' and let's chat about how.",
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/aisensy-6ac76be65560a38ce95633cd-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '800',
          minAge: '24',
          maxAge: '45',
          geoLocationDisplay: AISENSY_GEO,
          prediction: INR,
          selected: daily(800),
        },
      ],
    },
    {
      _id: '6ac76baaf686483b27338ea7',
      prompt: AISENSY_PROMPT,
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-08T10:08:42.698000',
      variants: [
        {
          _id: '6ac76bb21eb724743f3b285c',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'ctwa',
          adName: 'WhatsApp Business API (Free setup + Green Tick)',
          headline: 'WhatsApp Business API',
          headlineDescription: 'Unlock instant customer conversations.',
          adDescription:
            "Still seeing your email campaigns land in the 'Promotions' tab, unread? It's a familiar challenge for marketers.\n\nBut imagine connecting with your customers where they are truly active and responsive.\n\nAiSensy helps D2C and e-commerce brands ditch ignored emails for instant WhatsApp conversations. Built on the Official WhatsApp Business API, we empower you to automate campaigns, recover abandoned carts, and send updates with open rates email can only dream of.\n\nReady to boost engagement and drive more sales? Watch our video to see AiSensy in action.",
          callToAction: 'WHATSAPP_MESSAGE',
          ctaButton: '',
          websiteUrl: '',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/aisensy-6ac76baaf686483b27338ea7-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '900',
          minAge: '24',
          maxAge: '45',
          geoLocationDisplay: AISENSY_GEO,
          prediction: INR,
          selected: daily(900),
        },
      ],
    },
    {
      _id: '6ac63c129dce9cfc4ccbcd95',
      prompt:
        'create a video for headphone over the ear , with best in audio class and comfort with hiRes audio support , avilaible on flipkart and amazone\n\nbrand is Jabra and website is https://www.jabra.com/\n\naudience target is audio lover and tech lover age 16 to 50\n\nadd images from my website as well',
      templateCount: 1,
      aspectRatio: '4:3',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-07T12:33:22.127000',
      variants: [
        {
          _id: '6ac63c401eb724a2286b54d1',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'website_to_whatsapp',
          adName: 'Jabra Evolve2 85',
          headline: 'Quiet, even on a loud afternoon',
          headlineDescription: 'Hear every detail, feel nothing but comfort.',
          adDescription:
            "Let your focus flow with audio designed for clarity and comfort.\n\nThese over-ear wireless headphones deliver HiRes sound that pulls you into every note and conversation. Built for people who actually listen, they're light enough for hours of calls or your longest playlists.\n\nExperience audio that truly keeps pace with your day.",
          callToAction: 'SHOP_NOW',
          ctaButton: 'SHOP_NOW',
          websiteUrl: 'https://www.jabra.com',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/jabra-6ac63c129dce9cfc4ccbcd95-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '4:3',
          budgetType: 'daily_budget',
          dailyBudget: '500',
          minAge: '25',
          maxAge: '50',
          geoLocationDisplay: [BANGALORE, MUMBAI, DELHI, PUNE, HYDERABAD, CHENNAI],
          prediction: INR,
          selected: daily(500),
        },
      ],
    },
    {
      _id: '6ac631b452daa6cd3dac38c9',
      prompt:
        'create a video for headphone over the ear , with best in audio class and comfort with hiRes audio support , avilaible on flipkart and amazone\n\nbrand is Zabra and website is zabra.com\n\naudience target is audio lover and tech lover age 16 to 50',
      templateCount: 1,
      aspectRatio: '3:4',
      creativeFormat: 'image',
      status: 'completed',
      createdAt: '2026-10-07T11:49:08.296000',
      variants: [
        {
          _id: '6ac631dc1eb7244efe1d0344',
          variantIndex: 0,
          variantStatus: 'completed',
          adType: 'website_traffic',
          adName: 'Zabra Over-Ear Headphones',
          headline: 'Zabra Over-Ear Headphones',
          headlineDescription: 'Hear every detail. Feel every beat.',
          adDescription:
            "Ready to actually *feel* your music? Zabra headphones bring every beat to life with sound that truly hits different.\n\nYou're looking at:\n• Best-in-class audio for an immersive experience\n• Supreme comfort for hours of listening\n• Hi-Res audio support for pure sound\n\nFind your new favourite sound on Flipkart and Amazon today.",
          callToAction: 'SHOP_NOW',
          ctaButton: 'SHOP_NOW',
          websiteUrl: 'https://zabra.com',
          imageUrl:
            'https://d2rc167eibdk5g.cloudfront.net/video-ads/zabra-6ac631b452daa6cd3dac38c9-v0/cover.png',
          creativeFormat: 'image',
          aspectRatio: '3:4',
          budgetType: 'daily_budget',
          dailyBudget: '700',
          minAge: '18',
          maxAge: '34',
          geoLocationDisplay: [MUMBAI, BANGALORE, DELHI, PUNE, HYDERABAD, CHENNAI],
          prediction: INR,
          selected: daily(700),
        },
      ],
    },
  ],
};

export default adTemplatesResponse;
